import Footer from '../../Pages/separate/Footer'
import './training.css'
import '../../assets/style.css'
import NavbarOne from '../../Pages/separate/NavbarOne'
import {
  BookOpen,
  TrendingUp,
  Target,
  Shield,
  BarChart3,
  Zap,
  Play,
  Calendar,
  Users,
  Award,
  ExternalLink,
  Youtube,
  ArrowRight,
  Coins,
  Rocket,
} from 'lucide-react'

const workshopTopics = [
  {
    icon: BookOpen,
    title: 'Blockchain Basics',
    description:
      'Understand the fundamentals of blockchain technology, decentralization, and how cryptocurrencies work.',
  },
  {
    icon: BarChart3,
    title: 'Crypto Analysis',
    description:
      'Learn how to research and evaluate cryptocurrency projects before making investment decisions.',
  },
  {
    icon: TrendingUp,
    title: 'Technical Analysis',
    description:
      'Master chart patterns, indicators, and price action to predict market movements.',
  },
  {
    icon: Target,
    title: 'Fundamental Analysis',
    description:
      'Evaluate crypto projects based on technology, team, tokenomics, and market potential.',
  },
  {
    icon: Shield,
    title: 'Risk Management',
    description:
      'Learn stop loss strategies, position sizing, and how to protect your capital.',
  },
  {
    icon: Zap,
    title: 'Entry & Exit Points',
    description:
      'Identify optimal times to enter and exit trades for maximum profitability.',
  },
]

const tradingModules = [
  {
    id: 'spot',
    title: 'Spot Trading',
    subtitle: 'Buy & Sell Instantly',
    description:
      'Buy and sell cryptocurrencies at current market prices with instant settlement. Perfect for beginners and long-term investors.',
    features: [
      'Market Orders',
      'Limit Orders',
      'Order Book Analysis',
      'Trading Pairs',
    ],
    icon: Coins,
    color: 'from-primary/20 to-primary/5',
    borderColor: 'border-primary/30',
    stats: { risk: 'Low', leverage: '1x', settlement: 'Instant' },
  },
  {
    id: 'futures',
    title: 'Futures Trading',
    subtitle: 'Amplify Your Gains',
    description:
      'Trade cryptocurrency derivatives with leverage for amplified gains. For experienced traders seeking higher returns.',
    features: [
      'Leverage Trading',
      'Long & Short Positions',
      'Margin Management',
      'Liquidation Prevention',
    ],
    icon: Rocket,
    color: 'from-accent/20 to-accent/5',
    borderColor: 'border-accent/30',
    stats: { risk: 'High', leverage: 'Up to 125x', settlement: 'Contract' },
  },
]

const Training = (props) => {
  return (
    <div className="unitic-education-page">

      <NavbarOne setTheme={props.setTheme} />


      {/* =====================================================
               HERO
           ===================================================== */}

      <section className="education-section education-hero-section">

        <div className="education-hero-glow education-glow-left" />

        <div className="education-hero-glow education-glow-right" />


        <div className="education-container">

          <div className="education-hero-content">

            <div className="education-badge">

              <BookOpen size={16} />

              <span>
                Education Hub
              </span>

            </div>


            <h1>

              Master Crypto Trading With{" "}

              <span>
                Expert Guidance
              </span>

            </h1>


            <p>

              Join our one-day workshops and learn everything
              from blockchain basics to advanced trading
              strategies. Plus, access free educational content
              through our YouTube channel.

            </p>


            <div className="education-hero-actions">

              <a
                href="#workshops"
                className="education-primary-button"
              >

                <Calendar size={19} />

                Register for Workshop

              </a>


              <a
                href="https://www.youtube.com/@UniticExchange2025"
                target="_blank"
                rel="noopener noreferrer"
                className="education-outline-button"
              >

                <Youtube size={19} />

                Watch on YouTube

              </a>

            </div>


            <div className="education-stats-grid">

              {[
                {
                  value: "50+",
                  label: "Workshops Conducted"
                },
                {
                  value: "5000+",
                  label: "Students Trained"
                },
                {
                  value: "100+",
                  label: "Video Tutorials"
                },
                {
                  value: "4.9",
                  label: "Average Rating"
                }
              ].map((stat) => (

                <div
                  className="education-stat-item"
                  key={stat.label}
                >

                  <strong>
                    {stat.value}
                  </strong>

                  <span>
                    {stat.label}
                  </span>

                </div>

              ))}

            </div>

          </div>

        </div>

      </section>



      {/* =====================================================
               WORKSHOPS
           ===================================================== */}

      <section
        className="education-section education-soft-section"
        id="workshops"
      >

        <div className="education-container">

          <div className="education-section-heading">

            <h2>

              What You'll Learn In Our{" "}

              <span>
                Workshops
              </span>

            </h2>


            <p>

              Our comprehensive one-day workshops cover
              everything you need to start trading with more
              confidence.

            </p>

          </div>


          <div className="education-topic-grid">

            {workshopTopics.map((topic) => {

              const Icon = topic.icon;

              return (

                <div
                  className="education-topic-card"
                  key={topic.title}
                >

                  <div className="education-topic-icon">

                    <Icon size={25} />

                  </div>


                  <h3>
                    {topic.title}
                  </h3>


                  <p>
                    {topic.description}
                  </p>

                </div>

              );

            })}

          </div>

        </div>

      </section>



      {/* =====================================================
               TRADING MODULES
           ===================================================== */}

      <section className="education-section education-modules-section">

        <div className="education-module-glow education-module-glow-left" />

        <div className="education-module-glow education-module-glow-right" />


        <div className="education-container">

          <div className="education-section-heading">

            <div className="education-badge">

              <BarChart3 size={16} />

              <span>
                Master Both Methods
              </span>

            </div>


            <h2>

              Trading{" "}

              <span>
                Modules
              </span>

            </h2>


            <p>

              Deep dive into different trading methods with
              practical learning sessions.

            </p>

          </div>


          <div className="education-module-grid">

            {tradingModules.map((module) => {

              const Icon = module.icon;

              return (

                <div
                  className="education-module-card"
                  key={module.id}
                >

                  <div className="education-module-decoration" />


                  <div className="education-module-top">

                    <div>

                      <div className="education-module-badge">

                        <Icon size={15} />

                        <span>
                          {module.subtitle}
                        </span>

                      </div>


                      <h3>
                        {module.title}
                      </h3>

                    </div>


                    <div className="education-module-main-icon">

                      <Icon size={28} />

                    </div>

                  </div>


                  <p className="education-module-description">

                    {module.description}

                  </p>


                  <div className="education-module-stats">

                    {Object.entries(module.stats).map(
                      ([key, value]) => (

                        <div key={key}>

                          <span>
                            {key}
                          </span>

                          <strong>
                            {value}
                          </strong>

                        </div>

                      )
                    )}

                  </div>


                  <div className="education-module-features">

                    {module.features.map(
                      (feature, index) => (

                        <div
                          className="education-feature-item"
                          key={feature}
                        >

                          <span className="education-feature-number">

                            {index + 1}

                          </span>


                          <span>

                            {feature}

                          </span>

                        </div>

                      )
                    )}

                  </div>


                  <button
                    type="button"
                    className="education-module-button"
                  >

                    <span>

                      Learn {module.title}

                    </span>

                    <ArrowRight size={18} />

                  </button>

                </div>

              );

            })}

          </div>


          <div className="education-level-legend">

            <div>

              <span className="education-level-dot beginner" />

              Beginner Friendly

            </div>


            <span className="education-level-divider" />


            <div>

              <span className="education-level-dot advanced" />

              Advanced Traders

            </div>

          </div>

        </div>

      </section>



      {/* =====================================================
               YOUTUBE
           ===================================================== */}

      <section className="education-section education-soft-section">

        <div className="education-container">

          <div className="education-youtube-grid">

            <div className="education-youtube-content">

              <div className="education-youtube-badge">

                <Youtube size={16} />

                YouTube Channel

              </div>


              <h2>

                Free Education On{" "}

                <span className="youtube-title">

                  YouTube

                </span>

              </h2>


              <p>

                Can't attend a workshop? No problem!
                We continuously educate the crypto community
                through our YouTube channel with tutorials,
                market analysis, and trading strategies.

              </p>


              <ul className="education-youtube-list">

                {[
                  "Weekly market analysis videos",
                  "Step-by-step trading tutorials",
                  "Live trading sessions",
                  "Beginner-friendly content"
                ].map((item) => (

                  <li key={item}>

                    <span>

                      <Play size={11} />

                    </span>

                    {item}

                  </li>

                ))}

              </ul>


              <a
                href="https://www.youtube.com/@UniticExchange2025"
                target="_blank"
                rel="noopener noreferrer"
                className="education-primary-button"
              >

                <Youtube size={19} />

                Subscribe to Channel

                <ExternalLink size={16} />

              </a>

            </div>

            <div className="education-video-wrap">
<iframe width="560" height="315" src="https://www.youtube.com/embed/H-r3rd7patw?si=oNaUqSYX6Gu8rUxt" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>

              {/* <iframe
                src="https://www.youtube.com/embed/H-r3rd7patw?si=VV2rN8kzyO7x3Hi9"
                title="Unitic Exchange YouTube Channel"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              /> */}

            </div>

          </div>

        </div>

      </section>



      {/* =====================================================
               CTA
           ===================================================== */}

      <section className="education-section">

        <div className="education-container">

          <div className="education-cta-card">

            <div className="education-cta-content">

              <div>

                <h2>

                  Ready To Start Your{" "}

                  <span>

                    Trading Journey?

                  </span>

                </h2>


                <p>

                  Join our next workshop and learn from
                  industry experts. Limited seats available!

                </p>


                <a
                  href="#workshops"
                  className="education-primary-button"
                >

                  <Calendar size={19} />

                  Book Workshop Seat

                </a>

              </div>


              <div className="education-cta-stats">

                <div>

                  <Users size={30} />

                  <strong>
                    30
                  </strong>

                  <span>
                    Seats per batch
                  </span>

                </div>


                <div>

                  <Award size={30} />

                  <strong>
                    100%
                  </strong>

                  <span>
                    Hands-on training
                  </span>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      <Footer />

    </div>
  )
}

export default Training
